import { Inject, inject, Injectable } from '@angular/core';
import { CrmFieldService, PlatformExtensionFieldIds } from '@galaxy/crm/static';
import { CRMCompanyApiService, CrmObject, GetMultiCrmObjectRequest } from '@vendasta/crm';
import {
  CreateAccountGroupForCompanyStatus,
  PlatformApiService,
  GetAccountGroupForCompanyResponse,
} from '@vendasta/crm-integrations';
import { switchWhenFalsy } from '@vendasta/rx-utils';
import { ObservableWorkStateMap } from '@vendasta/rx-utils/work-state';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, retry, switchMap, take, tap } from 'rxjs/operators';

export interface CreateAccountGroupResponse {
  accountGroupId: string;
}

@Injectable()
export class CompanyAccountService {
  private readonly companyAPIService = inject(CRMCompanyApiService);
  private readonly platformApiService = inject(PlatformApiService);
  private readonly crmFieldService = inject(CrmFieldService);

  private readonly createAccountGroupState = new ObservableWorkStateMap<string, CreateAccountGroupResponse>();
  private readonly accountGroupId$$ = new ReplaySubject<string>(1);
  accountGroupId$ = this.accountGroupId$$.asObservable().pipe(distinctUntilChanged());

  constructor(@Inject('PARTNER_ID') private readonly partnerId$: Observable<string>) {}

  setAccountGroupId(accountGroupId: string): void {
    this.accountGroupId$$.next(accountGroupId);
  }

  getCompany$(companyId: string): Observable<CrmObject> {
    if (!companyId) {
      console.error('No company ID provided.');
      return of(null);
    }
    const sanityAccountGroupIdCheck$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.platformApiService.getAccountGroupForCompany({
          namespace: partnerId,
          companyId: companyId,
        });
      }),
      catchError(() => {
        return of(new GetAccountGroupForCompanyResponse({}));
      }),
      map((response) => response?.accountGroupId ?? ''),
    );
    const company$ = this.partnerId$.pipe(
      switchMap((id) => {
        const req = <GetMultiCrmObjectRequest>{
          namespace: id,
          crmObjectIds: [companyId],
        };
        return this.companyAPIService.getMultiCompany(req);
      }),
      catchError((err) => {
        console.error('Error fetching company', err);
        return of(null);
      }),
      map((resp) => {
        return resp?.crmObjects?.[0];
      }),
    );
    return combineLatest([company$, sanityAccountGroupIdCheck$]).pipe(
      map(([crmObject, accountGroupId]) => {
        for (const field of crmObject?.fields ?? []) {
          if (field.fieldId === PlatformExtensionFieldIds.AccountGroupID) {
            field.stringValue = accountGroupId;
          }
        }
        return crmObject;
      }),
    );
  }

  /**
   * Returns the AccountGroupID for the company.
   * If the company does not have an AccountGroup, one will be created and the AccountGroupID will be emitted to `accountGroupID$$`.
   */
  fetchAccountGroupId$(companyId: string): Observable<string> {
    const companyAccountGroupId$ = this.getCompany$(companyId).pipe(
      map((company) => {
        return this.crmFieldService.getFieldValueFromCrmObject(company, PlatformExtensionFieldIds.AccountGroupID)
          ?.stringValue;
      }),
    );
    const createAccountGroup$ = this.partnerId$.pipe(
      switchMap((partnerId) => this.doCreateAccountGroup(companyId, partnerId)),
      map((res) => res?.accountGroupId),
    );
    return this.accountGroupId$$.pipe(
      switchWhenFalsy(companyAccountGroupId$),
      take(1),
      switchMap((accountGroupId) => {
        return accountGroupId ? of(accountGroupId) : createAccountGroup$;
      }),
      tap((id) => (id ? this.accountGroupId$$.next(id) : null)),
    );
  }

  createAccountGroupSuccess$(companyId: string): Observable<boolean> {
    return this.createAccountGroupState.isSuccess$(companyId);
  }

  createAccountGroupLoading$(companyId: string): Observable<boolean> {
    return this.createAccountGroupState.isLoading$(companyId);
  }

  createAccountGroupResult$(companyId: string): Observable<CreateAccountGroupResponse> {
    return this.createAccountGroupLoading$(companyId).pipe(
      filter((loading) => !loading),
      switchMap(() => this.createAccountGroupSuccess$(companyId)),
      switchMap((success) => (success ? this.createAccountGroupState.getWorkResults$(companyId) : of(null))),
    );
  }

  doCreateAccountGroup(companyId: string, namespace: string): Observable<CreateAccountGroupResponse> {
    const work$ = this.createAccountGroupForCompany(companyId, namespace);
    this.createAccountGroupState.startWork(companyId, work$);
    return this.createAccountGroupResult$(companyId);
  }

  private createAccountGroupForCompany(companyId: string, namespace: string): Observable<CreateAccountGroupResponse> {
    const create$ = this.platformApiService
      .createAccountGroupForCompany({ companyId, namespace })
      .pipe(map((res) => res.ok));
    const watch$ = this.platformApiService.getCreateAccountGroupForCompanyStatus({ companyId, namespace }).pipe(
      map((accountStatus) => {
        switch (accountStatus?.status) {
          case CreateAccountGroupForCompanyStatus.CREATE_ACCOUNT_GROUP_FOR_COMPANY_STATUS_SUCCEEDED:
            return { accountGroupId: accountStatus.accountGroupId } as CreateAccountGroupResponse;
          case CreateAccountGroupForCompanyStatus.CREATE_ACCOUNT_GROUP_FOR_COMPANY_STATUS_FAILED:
            return null as CreateAccountGroupResponse;
          default:
            throw null;
        }
      }),
      retry({ delay: 1000, count: 30 }),
    );
    return create$.pipe(
      take(1),
      switchMap((res) => (res ? watch$ : of(null))),
    );
  }
}
